import React from 'react'
import { motion } from 'framer-motion'
import { GraduationCap, Calendar, Award, Users, BookOpen } from 'lucide-react'
import { Education as EducationType, Organization } from '../types'
import { fadeUp, staggerContainer, viewportOnce } from '../lib/motion'

interface EducationProps {
  education: EducationType
  organizations: Organization[]
}

const Education: React.FC<EducationProps> = ({ education, organizations }) => {
  return (
    <section id="education" className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="container-max section-padding">
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Education & Organizations
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full"></div>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-4 max-w-2xl mx-auto px-4">
            My academic journey and involvement in student organizations
          </p>
        </motion.div>

        <motion.div
          className="grid lg:grid-cols-2 gap-8 lg:gap-12"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          {/* Education Section */}
          <motion.div variants={fadeUp}>
            <div className="card p-6 sm:p-8 h-full">
              <div className="flex items-center mb-4 sm:mb-6">
                <div className="p-2 sm:p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg mr-3 sm:mr-4">
                  <GraduationCap className="h-6 w-6 sm:h-8 sm:w-8 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    Education
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Academic Background</p>
                </div>
              </div>

              <div className="space-y-4 sm:space-y-6">
                <div>
                  <h4 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {education.degree}
                  </h4>
                  <div className="text-sm sm:text-base text-primary-600 dark:text-primary-400 font-semibold mb-3">
                    {education.university}
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 mb-4">
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <Calendar className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      <span className="text-xs sm:text-sm">{education.duration}</span>
                    </div>
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <Award className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      <span className="text-xs sm:text-sm">GPA: {education.gpa}</span>
                    </div>
                  </div>

                  <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3 sm:p-4 mb-4">
                    <div className="text-xs sm:text-sm text-primary-700 dark:text-primary-300 font-medium mb-1">
                      Graduation Date
                    </div>
                    <div className="text-sm sm:text-base text-primary-600 dark:text-primary-400 font-semibold">
                      {education.graduationDate}
                    </div>
                  </div>

                  <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                    {education.description}
                  </p>
                </div>

                {/* Academic Highlights */}
                <div className="pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h5 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-3">
                    Academic Highlights
                  </h5>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div className="text-center p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="text-xl sm:text-2xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                        3.5
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Years to Graduate</div>
                    </div>
                    <div className="text-center p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="text-xl sm:text-2xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                        3.78
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Final GPA</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Organizations Section */}
          <motion.div variants={fadeUp}>
            <div className="card p-6 sm:p-8 h-full">
              <div className="flex items-center mb-4 sm:mb-6">
                <div className="p-2 sm:p-3 bg-accent-100 dark:bg-accent-900/30 rounded-lg mr-3 sm:mr-4">
                  <Users className="h-6 w-6 sm:h-8 sm:w-8 text-accent-600 dark:text-accent-400" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    Organizations
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Student Involvement</p>
                </div>
              </div>

              <div className="space-y-4 sm:space-y-6">
                {organizations.map((org, index) => (
                  <motion.div
                    key={index}
                    className="border-l-4 border-blue-200 dark:border-blue-800 pl-4 sm:pl-6 pb-4 sm:pb-6 last:pb-0"
                    whileHover={{ x: 4 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2">
                      <h4 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-1 sm:mb-0">
                        {org.name}
                      </h4>
                      <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                        {org.duration}
                      </span>
                    </div>
                    <div className="text-sm sm:text-base text-accent-600 dark:text-accent-400 font-semibold mb-2">
                      {org.position}
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm leading-relaxed">
                      {org.description}
                    </p>
                  </motion.div>
                ))}

                {/* Organization Stats */}
                <div className="pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700">
                  <h5 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mb-3">
                    Involvement Summary
                  </h5>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div className="text-center p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="text-xl sm:text-2xl font-bold text-accent-600 dark:text-accent-400 mb-1">
                        {organizations.length}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Organizations</div>
                    </div>
                    <div className="text-center p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="text-xl sm:text-2xl font-bold text-accent-600 dark:text-accent-400 mb-1">
                        2
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Years Active</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Additional Info */}
        <div className="mt-8 sm:mt-12">
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-2xl p-6 sm:p-8 text-center">
            <BookOpen className="h-8 w-8 sm:h-12 sm:w-12 text-primary-600 dark:text-primary-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Continuous Learning
            </h3>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed px-4">
              Beyond formal education, I'm committed to continuous learning and staying updated with the latest 
              technologies in backend development, cloud computing, and software architecture. I regularly 
              participate in online courses, workshops, and tech communities to enhance my skills.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Education